/**
 * AdminSubscriptions.jsx — Admin · Subscription Management
 * Design system: #0c1118 dark · #f9ab14 amber · Bebas Neue + DM Sans + DM Mono
 * Inline styles only — no Tailwind dependency.
 *
 * TABS (5 total):
 *  1. Plans & Pricing  — existing, preserved
 *  2. Trial Config     — NEW: admin sets trial days + features per context
 *  3. Coupons          — NEW: generate, list, send, revoke coupon codes
 *  4. Subscribers      — existing, enhanced with trial info + status filter
 *  5. Expired / Lapsed — NEW: users with incomplete/expired subscriptions
 */

import { useEffect, useState, useCallback } from "react";
import { BsCreditCard2FrontFill } from "react-icons/bs";
import { HiMiniUsers } from "react-icons/hi2";
import { FaPlus, FaUser, FaTag, FaPaintBrush, FaCube, FaSearch, FaEnvelope, FaBan, FaClock } from "react-icons/fa";
import { FaCheck } from "react-icons/fa";
import { IoMdAnalytics } from "react-icons/io";
import { BsFillPaletteFill } from "react-icons/bs";
import { ImCross } from "react-icons/im";
import { MdOutlineSearchOff, MdTimerOff } from "react-icons/md";
import { LuTimer, LuSettings2 } from "react-icons/lu";
import { useToast } from "../../components/ToastProvider";

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Design tokens ───────────────────────────────────────────────── */
const T = {
  fontDisplay: "'DM Sans', sans-serif",
  fontBody:    "'DM Sans', sans-serif",
  fontMono: "'DM Sans', sans-serif",
  amber:       "#111111",
  amberDark:   "#333333",
  amberBg:     "rgba(0,0,0,0.06)",
  amberBorder: "rgba(0,0,0,0.12)",
  dark:        "#0c1118",
  white:       "#ffffff",
  bg:          "#f4f6f8",
  surface:     "#ffffff",
  border:      "#e8ecf0",
  muted:       "#6b7280",
  mutedLight:  "#9ca3af",
  green:       "#16a34a",
  greenBg:     "rgba(34,197,94,0.1)",
  greenBorder: "rgba(34,197,94,0.25)",
  red:         "#dc2626",
  redBg:       "rgba(239,68,68,0.1)",
  redBorder:   "rgba(239,68,68,0.25)",
};

/* ── Inject styles once ──────────────────────────────────────────── */
if (typeof document !== "undefined" && !document.getElementById("asub-styles")) {
  const s = document.createElement("style");
  s.id = "asub-styles";
  s.textContent = `
    @keyframes asub-spin    { to { transform: rotate(360deg); } }
    @keyframes asub-fade-up { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
    .asub-spin     { animation: asub-spin 0.85s linear infinite; }
    .asub-input:focus { outline:none; border-color:#111111 !important; box-shadow:0 0 0 3px rgba(0,0,0,0.10); }
    .asub-row:hover   { background:rgba(0,0,0,0.03) !important; }
    .asub-exp:hover   { background:rgba(0,0,0,0.02) !important; }
    .asub-feat:hover  { background:rgba(0,0,0,0.04) !important; border-color:rgba(0,0,0,0.12) !important; }
    .asub-btn:hover   { opacity:0.82; }
    .asub-fade-up     { animation:asub-fade-up 0.2s ease both; }
  `;
  document.head.appendChild(s);
}

/* ── Shared atoms ───────────────────────────────────────────────── */
function Label({ children }) {
  return (
    <label style={{
      fontFamily: T.fontBody, fontSize: "0.62rem", letterSpacing: "0.08em",
       color: T.muted, display: "block", marginBottom: 6,
    }}>
      {children}
    </label>
  );
}

function FieldInput({ value, onChange, placeholder, type = "text", disabled }) {
  return (
    <input
      type={type}
      className="asub-input"
      disabled={disabled}
      style={{
        width: "100%", boxSizing: "border-box",
        padding: "9px 12px", borderRadius: 7,
        border: `1px solid ${T.border}`,
        fontFamily: T.fontBody, fontSize: "0.84rem", color: T.dark,
        background: disabled ? T.bg : T.surface,
        opacity: disabled ? 0.6 : 1,
      }}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
    />
  );
}

function FieldSelect({ value, onChange, children, disabled }) {
  return (
    <select
      className="asub-input"
      disabled={disabled}
      style={{
        width: "100%", boxSizing: "border-box",
        padding: "9px 12px", borderRadius: 7,
        border: `1px solid ${T.border}`,
        fontFamily: T.fontBody, fontSize: "0.84rem", color: T.dark,
        background: disabled ? T.bg : T.surface,
        opacity: disabled ? 0.6 : 1,
      }}
      value={value}
      onChange={onChange}
    >
      {children}
    </select>
  );
}

function Btn({ onClick, disabled, children, variant = "amber", small }) {
  const bg = {
    amber:   T.amber,
    dark:    T.dark,
    ghost:   T.bg,
    green:   "rgba(34,197,94,0.85)",
    red:     T.redBg,
    outline: "transparent",
  }[variant] || T.amber;
  const color = {
    amber:   T.dark,
    dark:    T.white,
    ghost:   T.dark,
    green:   T.white,
    red:     T.red,
    outline: T.muted,
  }[variant] || T.dark;
  const border = {
    ghost:   `1px solid ${T.border}`,
    red:     `1px solid ${T.redBorder}`,
    outline: `1px solid ${T.border}`,
  }[variant] || "none";

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="asub-btn"
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: small ? "5px 12px" : "8px 18px",
        borderRadius: 7, cursor: disabled ? "not-allowed" : "pointer",
        background: disabled ? T.mutedLight : bg,
        color: disabled ? T.white : color,
        border,
        fontFamily: T.fontBody,
        fontSize: small ? "0.77rem" : "0.82rem",
        fontWeight: 600,
        opacity: disabled ? 0.65 : 1,
        transition: "opacity 0.15s",
      }}
    >
      {children}
    </button>
  );
}

function Badge({ status }) {
  const map = {
    active:     { bg: T.greenBg,                      color: T.green,       border: T.greenBorder },
    trialing:   { bg: T.amberBg,                      color: T.amberDark,   border: T.amberBorder },
    past_due:   { bg: "rgba(234,179,8,0.1)",           color: "#a16207",     border: "rgba(234,179,8,0.3)" },
    incomplete: { bg: T.redBg,                        color: T.red,         border: T.redBorder },
    canceled:   { bg: "rgba(107,114,128,0.1)",         color: T.muted,       border: T.border },
    unpaid:     { bg: "rgba(249,115,22,0.1)",          color: "#c2410c",     border: "rgba(249,115,22,0.3)" },
    used:       { bg: T.greenBg,                       color: T.green,       border: T.greenBorder },
    expired:    { bg: "rgba(107,114,128,0.1)",         color: T.muted,       border: T.border },
    revoked:    { bg: T.redBg,                        color: T.red,         border: T.redBorder },
  };
  const s = map[status] || map.canceled;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "3px 10px", borderRadius: 99,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      fontFamily: T.fontBody, fontSize: "0.63rem",
      fontWeight: 600, letterSpacing: "0.06em", textTransform: "capitalize",
      whiteSpace: "nowrap",
    }}>
      {status?.replace(/_/g, " ") || "unknown"}
    </span>
  );
}

function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "40px 0", justifyContent: "center" }}>
      <div style={{ width: 20, height: 20, borderRadius: "50%", border: `2px solid ${T.border}`, borderTopColor: T.amber }} className="asub-spin" />
      <span style={{ fontFamily: T.fontBody, color: T.muted, fontSize: "0.86rem" }}>Loading…</span>
    </div>
  );
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function daysLeft(end) {
  if (!end) return null;
  const diff = new Date(end) - new Date();
  return Math.max(0, Math.ceil(diff / 86_400_000));
}

function getBool(list, key) {
  const f = list.find((x) => x.key === key);
  if (!f) return false;
  return !!(f.valueInt ?? 0);
}

/* ════════════════════════════════════════════════════════════════
   ROOT
   ════════════════════════════════════════════════════════════════ */
export default function AdminSubscriptions() {
  const [tab, setTab] = useState("plans");

  const TABS = [
    { key: "plans",   label: "Plans & Pricing",  Icon: BsCreditCard2FrontFill },
    { key: "trial",   label: "Trial Config",      Icon: LuTimer },
    { key: "coupons", label: "Coupons",            Icon: FaTag },
    { key: "subs",    label: "Subscribers",        Icon: HiMiniUsers },
    { key: "trials",  label: "Free Trials",        Icon: FaClock },
    { key: "expired", label: "Expired / Lapsed",   Icon: MdTimerOff },
  ];

  return (
    <div style={{ fontFamily: T.fontBody }}>
      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 24, fontWeight: 700, color: "#0c1118", margin: 0, lineHeight: 1.2 }}>
          Subscription Management
        </h1>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#6b7280", margin: "4px 0 0" }}>
          Manage plans, trials, coupons, and subscriber activity
        </p>
      </div>

      {/* Tab bar */}
      <div style={{ marginBottom: 24, overflowX: "auto" }}>
        <div style={{
          display: "inline-flex", background: T.surface,
          border: `1px solid ${T.border}`, borderRadius: 10, padding: 4, gap: 2,
        }}>
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{
                display: "flex", alignItems: "center", gap: 7,
                padding: "8px 16px", borderRadius: 7, cursor: "pointer",
                background: tab === key ? T.dark : "transparent",
                color:      tab === key ? T.white : T.muted,
                border: "none",
                fontFamily: T.fontBody, fontSize: "0.83rem",
                fontWeight: tab === key ? 600 : 400,
                transition: "all 0.15s",
                whiteSpace: "nowrap",
              }}
            >
              <Icon style={{ fontSize: 14 }} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab === "plans"   && <PlansPanel />}
      {tab === "trial"   && <TrialConfigPanel />}
      {tab === "coupons" && <CouponsPanel />}
      {tab === "subs"    && <SubscribersPanel />}
      {tab === "trials"  && <FreeTrialsPanel />}
      {tab === "expired" && <ExpiredPanel />}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   TAB 1 — PLANS & PRICING (preserved from original, unchanged)
   ════════════════════════════════════════════════════════════════ */
function PlansPanel() {
  const [plans, setPlans] = useState([]);

  async function load() {
    const r = await fetch(`${API}/api/admin/plans`, { credentials: "include" });
    const d = await r.json();
    setPlans(d.items || []);
  }
  useEffect(() => { load(); }, []);

  const individualPlans = plans.filter((p) => p.context === "individual");
  const orgPlans        = plans.filter((p) => p.context === "organization");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
      <PlanGroup
        context="individual"
        label="Individual Plans"
        sublabel="Plans for solo users — Free, Basic, Professional, Expert"
        accentColor={T.amberDark}
        plans={individualPlans}
        onReload={load}
      />
      <PlanGroup
        context="organization"
        label="Company Plans"
        sublabel="Plans for organizations — Standard, Premium, Enterprise"
        accentColor={T.amberDark}
        plans={orgPlans}
        onReload={load}
      />
    </div>
  );
}

/* ── Plan Group — one section per context ───────────────────────── */
function PlanGroup({ context, label, sublabel, accentColor, plans, onReload }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({ code: "", name: "", description: "", context });
  const [busy, setBusy]         = useState(false);
  const isOrg = context === "organization";

  async function createPlan() {
    setBusy(true);
    try {
      await fetch(`${API}/api/admin/plans`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...form, context }),
      });
      setForm({ code: "", name: "", description: "", context });
      setShowForm(false);
      onReload();
    } finally { setBusy(false); }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Section header */}
      <div style={{
        background: T.surface, borderRadius: 10,
        border: `1px solid ${T.border}`,
        borderLeft: `4px solid ${accentColor}`,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        overflow: "hidden",
      }}>
        <div style={{ padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 34, height: 34, borderRadius: 8, background: accentColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {isOrg
                ? <HiMiniUsers style={{ color: "#fff", fontSize: 16 }} />
                : <FaUser      style={{ color: "#fff", fontSize: 13 }} />}
            </div>
            <div>
              <h2 style={{ fontFamily: T.fontBody, fontSize: "1.15rem", letterSpacing: "0.04em", color: T.dark, margin: 0 }}>
                {label}
              </h2>
              <p style={{ fontFamily: T.fontBody, fontSize: "0.78rem", color: T.muted, margin: "2px 0 0" }}>
                {plans.length} plan{plans.length !== 1 ? "s" : ""} · {sublabel}
              </p>
            </div>
          </div>
          <Btn onClick={() => setShowForm(!showForm)} variant={showForm ? "ghost" : "ghost"}>
            {showForm ? <><ImCross size={10} /> Cancel</> : <><FaPlus size={11} /> New {isOrg ? "Company" : "Individual"} Plan</>}
          </Btn>
        </div>

        {showForm && (
          <div style={{ padding: "18px 22px", borderTop: `1px solid ${T.border}`, background: T.bg }}>
            <p style={{ fontFamily: T.fontBody, fontSize: "0.62rem", letterSpacing: "0.1em",  color: T.muted, margin: "0 0 12px" }}>
              Create New {isOrg ? "Company" : "Individual"} Plan
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))", gap: 12 }}>
              <div><Label>Plan Code</Label><FieldInput value={form.code} placeholder={isOrg ? "e.g., enterprise" : "e.g., pro"} onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
              <div><Label>Display Name</Label><FieldInput value={form.name} placeholder={isOrg ? "e.g., Enterprise" : "e.g., Professional"} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Description</Label><FieldInput value={form.description} placeholder="Brief description" onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
              <Btn onClick={createPlan} disabled={busy || !form.code || !form.name}>
                {busy ? "Creating…" : "Create Plan"}
              </Btn>
            </div>
          </div>
        )}
      </div>

      {/* Plan cards for this context */}
      {plans.length === 0 ? (
        <div style={{
          background: T.surface, borderRadius: 10, border: `1px dashed ${T.border}`,
          padding: "32px 24px", textAlign: "center",
        }}>
          <p style={{ fontFamily: T.fontBody, color: T.muted, fontSize: "0.86rem", margin: 0 }}>
            No {isOrg ? "company" : "individual"} plans yet. Create one above.
          </p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px,1fr))", gap: 16 }}>
          {plans.map((plan) => <PlanCard key={plan.id} plan={plan} onChange={onReload} />)}
        </div>
      )}
    </div>
  );
}

/* ── Plan Card (preserved from original) ───────────────────────── */
function PlanCard({ plan, onChange }) {
  const [meta, setMeta]         = useState({ name: plan.name, description: plan.description, active: plan.active, context: plan.context || "individual" });
  const [priceForm, setPriceForm] = useState({ interval: "month", unitAmount: "", currency: "usd", trialDays: "" });
  const [features, setFeatures] = useState(plan.features || []);
  const [busy, setBusy]         = useState(false);
  const [expanded, setExpanded] = useState(null);
  const toast = useToast();

  async function saveMeta() {
    setBusy(true);
    try {
      await fetch(`${API}/api/admin/plans/${plan.id}`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(meta) });
      onChange();
    } finally { setBusy(false); }
  }

  async function addPrice() {
    setBusy(true);
    try {
      await fetch(`${API}/api/admin/plans/${plan.id}/prices`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currency: priceForm.currency, unitAmount: Number(priceForm.unitAmount), interval: priceForm.interval, trialDays: priceForm.trialDays ? Number(priceForm.trialDays) : undefined }),
      });
      setPriceForm({ interval: "month", unitAmount: "", currency: "usd", trialDays: "" });
      onChange();
    } finally { setBusy(false); }
  }

  async function deactivatePrice(priceId) {
    setBusy(true);
    try {
      await fetch(`${API}/api/admin/prices/${priceId}/deactivate`, { method: "PATCH", credentials: "include" });
      onChange();
    } finally { setBusy(false); }
  }

  async function saveFeatures() {
    setBusy(true);
    try {
      await fetch(`${API}/api/admin/plans/${plan.id}/features`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ features }) });
      onChange();
    } finally { setBusy(false); }
  }

  async function saveDisplayedDiscounts() {
    setBusy(true);
    try {
      const monthPct = features.find((f) => f.key === "price.month.discount_percent")?.valueInt || 0;
      const yearPct  = features.find((f) => f.key === "price.year.discount_percent")?.valueInt  || 0;
      const res = await fetch(`${API}/api/admin/plans/${plan.id}/discounts`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ monthPercent: monthPct, yearPercent: yearPct }) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onChange();
    } catch (e) {
      toast.error(`Failed to save discounts: ${e.message}`);
    } finally { setBusy(false); }
  }

  function setFeature(key, patch) {
    setFeatures((prev) => {
      const idx = prev.findIndex((f) => f.key === key);
      if (idx === -1) return [...prev, { key, ...patch }];
      const next = [...prev]; next[idx] = { ...next[idx], ...patch }; return next;
    });
  }

  const monthPct     = features.find((f) => f.key === "price.month.discount_percent")?.valueInt ?? 0;
  const yearPct      = features.find((f) => f.key === "price.year.discount_percent")?.valueInt  ?? 0;
  const activePrices = plan.prices.filter((p) => p.active);
  const isOrg        = (meta.context || plan.context) === "organization";

  return (
    <div style={{ background: T.surface, borderRadius: 10, border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
      <div style={{ padding: "18px 22px", borderBottom: `1px solid ${T.border}` }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontFamily: T.fontBody, fontSize: "1.3rem", letterSpacing: "0.04em", color: T.dark }}>{plan.code}</span>
              <Badge status={meta.active ? "active" : "canceled"} />
              <span style={{ padding: "2px 10px", borderRadius: 99, fontSize: "0.65rem", fontWeight: 600, fontFamily: T.fontBody, letterSpacing: "0.06em",  background: T.amberBg, color: T.amberDark, border: `1px solid ${T.amberBorder}` }}>
                {isOrg ? "Company" : "Individual"}
              </span>
            </div>
            <p style={{ fontFamily: T.fontBody, fontSize: "0.8rem", color: T.muted, margin: "4px 0 0" }}>{meta.description || "No description"}</p>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <p style={{ fontFamily: T.fontBody, fontSize: "0.6rem", letterSpacing: "0.08em",  color: T.muted, margin: 0 }}>Prices</p>
            <p style={{ fontFamily: T.fontBody, fontSize: "1.6rem", fontWeight: 700, color: T.dark, margin: "2px 0 0", lineHeight: 1 }}>{activePrices.length}</p>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px,1fr))", gap: 10 }}>
          <div><Label>Plan Name</Label><FieldInput value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} /></div>
          <div><Label>Description</Label><FieldInput value={meta.description || ""} onChange={(e) => setMeta({ ...meta, description: e.target.value })} /></div>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontFamily: T.fontBody, fontSize: "0.84rem", color: T.dark }}>
            <input type="checkbox" checked={!!meta.active} onChange={(e) => setMeta({ ...meta, active: e.target.checked })} style={{ width: 15, height: 15, accentColor: T.amber }} />
            Plan is active
          </label>
          <Btn onClick={saveMeta} disabled={busy} variant="ghost">{busy ? "Saving…" : "Save Changes"}</Btn>
        </div>
      </div>

      <ExpandableSection title="Pricing" subtitle={`${activePrices.length} active price${activePrices.length !== 1 ? "s" : ""}`} isExpanded={expanded === "pricing"} onToggle={() => setExpanded(expanded === "pricing" ? null : "pricing")}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {activePrices.map((price) => (
            <div key={price.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: T.bg, borderRadius: 8, border: `1px solid ${T.border}` }}>
              <div>
                <p style={{ fontFamily: T.fontBody, fontSize: "1.1rem", color: T.dark, margin: 0 }}>${price.unitAmount.toFixed(2)} {price.currency.toUpperCase()}</p>
                <p style={{ fontFamily: T.fontBody, fontSize: "0.78rem", color: T.muted, margin: "2px 0 0", textTransform: "capitalize" }}>{price.interval}ly billing</p>
              </div>
              <Btn onClick={() => deactivatePrice(price.id)} disabled={busy} variant="red" small>Deactivate</Btn>
            </div>
          ))}
          <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
            <p style={{ fontFamily: T.fontBody, fontSize: "0.62rem", letterSpacing: "0.1em",  color: T.muted, margin: "0 0 10px" }}>Add New Price</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px,1fr))", gap: 8 }}>
              <FieldSelect value={priceForm.interval} onChange={(e) => setPriceForm({ ...priceForm, interval: e.target.value })}><option value="month">Monthly</option><option value="year">Yearly</option></FieldSelect>
              <FieldInput value={priceForm.unitAmount} placeholder="Amount" onChange={(e) => setPriceForm({ ...priceForm, unitAmount: e.target.value })} />
              <FieldInput value={priceForm.currency} placeholder="Currency" onChange={(e) => setPriceForm({ ...priceForm, currency: e.target.value })} />
              <FieldInput value={priceForm.trialDays} placeholder="Trial days" onChange={(e) => setPriceForm({ ...priceForm, trialDays: e.target.value })} />
            </div>
            <div style={{ marginTop: 10 }}>
              <Btn onClick={addPrice} disabled={busy || !priceForm.unitAmount}>Add Price</Btn>
            </div>
          </div>
        </div>
      </ExpandableSection>

      <ExpandableSection title="Features" subtitle={`Configure plan capabilities (${isOrg ? "Company" : "Individual"})`} isExpanded={expanded === "features"} onToggle={() => setExpanded(expanded === "features" ? null : "features")}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {isOrg ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <Label>Company projects limit</Label>
                <FieldInput type="number" value={features.find((f) => f.key === "org.projects.max")?.valueInt ?? ""} onChange={(e) => setFeature("org.projects.max", { valueInt: Number(e.target.value), valueString: null, valueJson: null })} />
              </div>
              <div>
                <Label>Per-member projects limit</Label>
                <FieldInput type="number" value={features.find((f) => f.key === "member.projects.max")?.valueInt ?? ""} onChange={(e) => setFeature("member.projects.max", { valueInt: Number(e.target.value), valueString: null, valueJson: null })} />
              </div>
            </div>
          ) : (
            <div><Label>Projects limit</Label><FieldInput type="number" value={features.find((f) => f.key === "projects.max")?.valueInt ?? ""} onChange={(e) => setFeature("projects.max", { valueInt: Number(e.target.value), valueString: null, valueJson: null })} /></div>
          )}
          <div>
            <Label>Available Features</Label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {[
                { key: "feat.3d",        label: "3D Tools",        Icon: FaCube },
                { key: "feat.2d",        label: "2D Tools",        Icon: FaPaintBrush },
                ...(isOrg ? [{ key: "feat.branding", label: "Custom Branding", Icon: BsFillPaletteFill }] : []),
                { key: "feat.estimator", label: "Auto Estimator",  Icon: IoMdAnalytics },
              ].map(({ key, label, Icon }) => (
                <label key={key} className="asub-feat" style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, border: `1px solid ${T.border}`, cursor: "pointer", transition: "all 0.12s" }}>
                  <input type="checkbox" checked={getBool(features, key)} onChange={(e) => setFeature(key, { valueInt: e.target.checked ? 1 : 0, valueString: null, valueJson: null })} style={{ width: 15, height: 15, accentColor: T.amber }} />
                  <Icon style={{ fontSize: 16, color: T.amber, flexShrink: 0 }} />
                  <span style={{ fontFamily: T.fontBody, fontSize: "0.84rem", fontWeight: 600, color: T.dark }}>{label}</span>
                </label>
              ))}
            </div>
          </div>
          <Btn onClick={saveFeatures} disabled={busy} variant="green">{busy ? "Saving…" : "Save Features"}</Btn>
        </div>
      </ExpandableSection>

      <ExpandableSection title="Promotional Discounts" subtitle="Configure checkout discounts" isExpanded={expanded === "discounts"} onToggle={() => setExpanded(expanded === "discounts" ? null : "discounts")}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><Label>Monthly Discount (%)</Label><FieldInput type="number" value={monthPct} onChange={(e) => setFeature("price.month.discount_percent", { valueInt: Number(e.target.value) || 0, valueString: null, valueJson: null })} /></div>
            <div><Label>Annual Discount (%)</Label><FieldInput type="number" value={yearPct} onChange={(e) => setFeature("price.year.discount_percent", { valueInt: Number(e.target.value) || 0, valueString: null, valueJson: null })} /></div>
          </div>
          <Btn onClick={saveDisplayedDiscounts} disabled={busy} variant="amber">{busy ? "Saving…" : "Save Discounts"}</Btn>
        </div>
      </ExpandableSection>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   TAB 2 — TRIAL CONFIG (full version with limits)
   ════════════════════════════════════════════════════════════════ */
const TRIAL_FEATURE_DEFS = [
  { key: "feat.3d",        label: "3D Builder",      Icon: FaCube,            contexts: ["individual", "organization"] },
  { key: "feat.2d",        label: "2D Drawing Tools", Icon: FaPaintBrush,      contexts: ["individual", "organization"] },
  { key: "feat.estimator", label: "Auto Estimator",   Icon: IoMdAnalytics,     contexts: ["individual", "organization"] },
  { key: "feat.branding",  label: "Custom Branding",  Icon: BsFillPaletteFill, contexts: ["individual", "organization"] }, // FIX: now configurable; org trial enforcement is in assignOrgTrial() backend
];

const LIMIT_DEFS = {
  individual: [
    { key: "projects.max", label: "Project Limit", hint: "Max number of projects an individual user can create during the trial." },
  ],
  organization: [
    { key: "org.projects.max",    label: "Company Project Limit",    hint: "Total projects the company can create during the trial." },
    { key: "member.projects.max", label: "Per-Member Project Limit", hint: "Max active projects any single member can be assigned." },
    { key: "members.max",         label: "Member / User Limit",      hint: "Maximum number of staff/member accounts the org can invite during the trial." },
  ],
};

function TrialConfigPanel() {
  const [configs, setConfigs] = useState({ individual: null, organization: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState({ individual: false, organization: false });
  const toast = useToast();

  async function load() {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/trial-config`, { credentials: "include" });
      const d = await r.json();
      // Ensure limitSettings is always an object
      const normalise = (cfg) => ({
        ...cfg,
        limitSettings: cfg.limitSettings || {},
      });
      setConfigs({ individual: normalise(d.individual), organization: normalise(d.organization) });
    } catch { toast.error("Failed to load trial config"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save(context) {
    setSaving((p) => ({ ...p, [context]: true }));
    try {
      const cfg = configs[context];
      const res = await fetch(`${API}/api/admin/trial-config/${context}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          trialDays:       cfg.trialDays,
          allowedFeatures: cfg.allowedFeatures,
          limitSettings:   cfg.limitSettings,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`${context === "individual" ? "Individual" : "Company"} trial config saved`);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving((p) => ({ ...p, [context]: false })); }
  }

  function toggleFeature(context, key) {
    setConfigs((prev) => {
      const cfg = prev[context];
      const has = cfg.allowedFeatures.includes(key);
      return {
        ...prev,
        [context]: {
          ...cfg,
          allowedFeatures: has
            ? cfg.allowedFeatures.filter((k) => k !== key)
            : [...cfg.allowedFeatures, key],
        },
      };
    });
  }

  function setLimit(context, key, value) {
    setConfigs((prev) => ({
      ...prev,
      [context]: {
        ...prev[context],
        limitSettings: {
          ...prev[context].limitSettings,
          [key]: value === "" ? "" : Number(value),
        },
      },
    }));
  }

  if (loading) return <Spinner />;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px,1fr))", gap: 20 }}>
      {["individual", "organization"].map((ctx) => {
        const cfg = configs[ctx];
        if (!cfg) return null;
        const isOrg = ctx === "organization";
        const limitDefs = LIMIT_DEFS[ctx];

        return (
          <div key={ctx} style={{ background: T.surface, borderRadius: 10, border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>

            {/* Card header */}
            <div style={{ padding: "16px 22px", borderBottom: `1px solid ${T.border}`, background: T.bg, display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: T.dark, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {isOrg ? <HiMiniUsers style={{ color: T.white, fontSize: 16 }} /> : <FaUser style={{ color: T.white, fontSize: 14 }} />}
              </div>
              <div>
                <p style={{ fontFamily: T.fontBody, fontSize: "1.1rem", letterSpacing: "0.04em", color: T.dark, margin: 0 }}>
                  {isOrg ? "Company Account Trial" : "Individual Account Trial"}
                </p>
                <p style={{ fontFamily: T.fontBody, fontSize: "0.78rem", color: T.muted, margin: "2px 0 0" }}>
                  {isOrg ? "Applied when a new organization is created" : "Applied when a new individual user registers"}
                </p>
              </div>
            </div>

            <div style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: 22 }}>

              {/* ── Trial Duration ───────────────────────────────── */}
              <div>
                <Label>Trial Duration (days)</Label>
                <FieldInput
                  type="number"
                  value={cfg.trialDays}
                  onChange={(e) => setConfigs((prev) => ({ ...prev, [ctx]: { ...prev[ctx], trialDays: Number(e.target.value) } }))}
                />
                <p style={{ fontFamily: T.fontBody, fontSize: "0.73rem", color: T.muted, margin: "5px 0 0" }}>
                  After this period, the subscription flips to <strong>incomplete</strong> and all access is blocked until payment.
                </p>
              </div>

              {/* ── Numeric Limits ───────────────────────────────── */}
              <div>
                <p style={{ fontFamily: T.fontBody, fontSize: "0.62rem", letterSpacing: "0.1em",  color: T.muted, margin: "0 0 10px" }}>
                  Limits During Trial
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {limitDefs.map(({ key, label, hint }) => (
                    <div key={key} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "12px 14px" }}>
                      <Label>{label}</Label>
                      <FieldInput
                        type="number"
                        value={cfg.limitSettings[key] ?? ""}
                        placeholder="e.g. 3"
                        onChange={(e) => setLimit(ctx, key, e.target.value)}
                      />
                      <p style={{ fontFamily: T.fontBody, fontSize: "0.72rem", color: T.muted, margin: "5px 0 0" }}>{hint}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Feature Toggles ──────────────────────────────── */}
              <div>
                <p style={{ fontFamily: T.fontBody, fontSize: "0.62rem", letterSpacing: "0.1em",  color: T.muted, margin: "0 0 10px" }}>
                  Features Available During Trial
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {TRIAL_FEATURE_DEFS.map(({ key, label, Icon, locked }) => {
                    const isChecked = cfg.allowedFeatures.includes(key);

                    return (
                      <label
                        key={key}
                        className={locked ? "" : "asub-feat"}
                        style={{
                          display: "flex", alignItems: "center", gap: 10,
                          padding: "10px 12px", borderRadius: 8,
                          border: `1px solid ${T.border}`,
                          background: locked ? T.bg : T.surface,
                          cursor: locked ? "not-allowed" : "pointer",
                          opacity: locked ? 0.5 : 1,
                          transition: "all 0.12s",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={locked ? false : isChecked}
                          disabled={locked}
                          onChange={() => !locked && toggleFeature(ctx, key)}
                          style={{ width: 15, height: 15, accentColor: T.amber }}
                        />
                        <Icon style={{ fontSize: 15, color: locked ? T.mutedLight : T.amber, flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <span style={{ fontFamily: T.fontBody, fontSize: "0.84rem", fontWeight: 600, color: locked ? T.muted : T.dark }}>{label}</span>
                          {locked && (
                            <span style={{ marginLeft: 8, fontFamily: T.fontBody, fontSize: "0.6rem", color: T.red, letterSpacing: "0.06em", }}>
                              Paid tier only — never in trial
                            </span>
                          )}
                        </div>
                        {/* Show current state clearly */}
                        {!locked && (
                          <span style={{ fontFamily: T.fontBody, fontSize: "0.6rem", letterSpacing: "0.06em",  color: isChecked ? T.green : T.muted }}>
                            {isChecked ? "On" : "Off"}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* ── Info note ────────────────────────────────────── */}
              <div style={{ background: T.amberBg, border: `1px solid ${T.amberBorder}`, borderRadius: 8, padding: "12px 14px" }}>
                <p style={{ fontFamily: T.fontBody, fontSize: "0.8rem", color: T.amberDark, margin: 0, lineHeight: 1.5 }}>
                  <strong>Note:</strong> Changes only affect <em>new</em> accounts created after saving. Existing trials keep their original snapshot.
                </p>
              </div>

              <Btn onClick={() => save(ctx)} disabled={saving[ctx]} variant="ghost">
                <LuSettings2 size={13} />
                {saving[ctx] ? "Saving…" : "Save Trial Config"}
              </Btn>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   TAB 3 — COUPONS (NEW)
   ════════════════════════════════════════════════════════════════ */
function CouponsPanel() {
  const [coupons, setCoupons]     = useState([]);
  const [stats, setStats]         = useState({ total: 0, active: 0, used: 0, expired: 0, revoked: 0 });
  const [loading, setLoading]     = useState(false);
  const [statusFilter, setFilter] = useState("all");
  const [search, setSearch]       = useState("");
  const [showForm, setShowForm]   = useState(false);
  const [users, setUsers]         = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const toast = useToast();

  const [form, setForm] = useState({
    issuedToUserId: "",
    discountType: "percentage",
    discountValue: "",
    expiresAt: "",
    maxUses: 1,
    planContext: "",
    notes: "",
    sendEmail: true,
  });
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (search) params.set("q", search);
      const [cRes, sRes] = await Promise.all([
        fetch(`${API}/api/admin/coupons?${params}`, { credentials: "include" }),
        fetch(`${API}/api/admin/coupons/stats`, { credentials: "include" }),
      ]);
      const cData = await cRes.json();
      const sData = await sRes.json();
      setCoupons(cData.items || []);
      setStats(sData);
    } finally { setLoading(false); }
  }, [statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  async function searchUsers(q) {
    if (!q || q.length < 2) { setUsers([]); return; }
    try {
      const r = await fetch(`${API}/api/admin/users?q=${encodeURIComponent(q)}&limit=10`, { credentials: "include" });
      const d = await r.json();
      setUsers(d.users || d.items || []);
    } catch { setUsers([]); }
  }

  async function generate() {
    if (!form.discountValue) return;
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/admin/coupons`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          issuedToUserId: form.issuedToUserId || null,
          planContext: form.planContext || null,
          discountValue: Number(form.discountValue),
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.message || "Failed");
      toast.success(`Coupon ${d.coupon.code} generated${form.sendEmail && form.issuedToUserId ? " and emailed" : ""}`);
      setShowForm(false);
      setForm({ issuedToUserId: "", discountType: "percentage", discountValue: "", expiresAt: "", maxUses: 1, planContext: "", notes: "", sendEmail: true });
      setUsers([]);
      setUserSearch("");
      load();
    } catch (e) { toast.error(e.message); }
    finally { setGenerating(false); }
  }

  async function sendEmail(id) {
    try {
      const res = await fetch(`${API}/api/admin/coupons/${id}/send`, { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error("Failed to send");
      toast.success("Coupon email sent");
      load();
    } catch (e) { toast.error(e.message); }
  }

  async function revoke(id) {
    try {
      const res = await fetch(`${API}/api/admin/coupons/${id}/revoke`, { method: "PATCH", credentials: "include" });
      if (!res.ok) throw new Error("Failed to revoke");
      toast.success("Coupon revoked");
      load();
    } catch (e) { toast.error(e.message); }
  }

  const STAT_CARDS = [
    { label: "Total",   value: stats.total,   accent: T.dark,      key: "all" },
    { label: "Active",  value: stats.active,  accent: T.green,     key: "active" },
    { label: "Used",    value: stats.used,    accent: "#6366f1",   key: "used" },
    { label: "Expired", value: stats.expired, accent: T.muted,     key: "expired" },
    { label: "Revoked", value: stats.revoked, accent: T.red,       key: "revoked" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Stats row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px,1fr))", gap: 12 }}>
        {STAT_CARDS.map(({ label, value, accent, key }) => (
          <div
            key={key}
            onClick={() => setFilter(key)}
            style={{
              background: T.surface, borderRadius: 8, padding: "14px 16px",
              border: `1px solid ${statusFilter === key ? accent : T.border}`,
              borderLeft: `3px solid ${accent}`,
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              cursor: "pointer", transition: "border-color 0.15s",
            }}
          >
            <p style={{ fontFamily: T.fontBody, fontSize: "0.58rem", letterSpacing: "0.1em",  color: T.muted, margin: 0 }}>{label}</p>
            <p style={{ fontFamily: T.fontBody, fontSize: "1.8rem", fontWeight: 700, color: T.dark, margin: "4px 0 0", lineHeight: 1 }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Main card */}
      <div style={{ background: T.surface, borderRadius: 10, border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        {/* Toolbar */}
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
            <div>
              <h2 style={{ fontFamily: T.fontBody, fontSize: "1.2rem", letterSpacing: "0.04em", color: T.dark, margin: 0 }}>Coupon Codes</h2>
              <p style={{ fontFamily: T.fontBody, fontSize: "0.8rem", color: T.muted, margin: "3px 0 0" }}>Generate and manage promotional codes for users</p>
            </div>
            <Btn onClick={() => setShowForm(!showForm)} variant={showForm ? "ghost" : "ghost"}>
              {showForm ? <><ImCross size={10} /> Cancel</> : <><FaPlus size={11} /> Generate Coupon</>}
            </Btn>
          </div>

          {/* Search + filter */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
              <FaSearch style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: T.mutedLight, fontSize: 13 }} />
              <input
                className="asub-input"
                style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px 9px 34px", borderRadius: 7, border: `1px solid ${T.border}`, fontFamily: T.fontBody, fontSize: "0.84rem", color: T.dark, background: T.surface }}
                placeholder="Search by code or user email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
              />
            </div>
            <FieldSelect value={statusFilter} onChange={(e) => setFilter(e.target.value)} style={{ width: 140 }}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="used">Used</option>
              <option value="expired">Expired</option>
              <option value="revoked">Revoked</option>
            </FieldSelect>
            <Btn onClick={load} disabled={loading} variant="ghost">{loading ? "…" : "Search"}</Btn>
          </div>
        </div>

        {/* Generate form */}
        {showForm && (
          <div style={{ padding: "20px 22px", borderBottom: `1px solid ${T.border}`, background: T.bg }} className="asub-fade-up">
            <p style={{ fontFamily: T.fontBody, fontSize: "0.62rem", letterSpacing: "0.1em",  color: T.muted, margin: "0 0 16px" }}>Generate New Coupon Code</p>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px,1fr))", gap: 14 }}>
              {/* User picker */}
              <div style={{ gridColumn: "1 / -1" }}>
                <Label>Issue To User (optional — leave blank for public code)</Label>
                <div style={{ position: "relative" }}>
                  <FaSearch style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: T.mutedLight, fontSize: 13 }} />
                  <input
                    className="asub-input"
                    style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px 9px 34px", borderRadius: 7, border: `1px solid ${T.border}`, fontFamily: T.fontBody, fontSize: "0.84rem", color: T.dark, background: T.surface }}
                    placeholder="Search user by email or name…"
                    value={userSearch}
                    onChange={(e) => { setUserSearch(e.target.value); searchUsers(e.target.value); }}
                  />
                </div>
                {users.length > 0 && (
                  <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, background: T.surface, marginTop: 4, overflow: "hidden", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
                    {users.map((u) => (
                      <div
                        key={u.id}
                        onClick={() => { setForm({ ...form, issuedToUserId: u.id }); setUserSearch(`${u.firstname} ${u.lastname} (${u.email})`); setUsers([]); }}
                        style={{ padding: "10px 14px", cursor: "pointer", borderBottom: `1px solid ${T.border}`, fontFamily: T.fontBody, fontSize: "0.84rem", color: T.dark, display: "flex", alignItems: "center", gap: 8 }}
                        onMouseEnter={(e) => e.currentTarget.style.background = T.amberBg}
                        onMouseLeave={(e) => e.currentTarget.style.background = ""}
                      >
                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: T.dark, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <span style={{ fontFamily: T.fontBody, fontSize: "0.9rem", color: "#ffffff" }}>{u.firstname?.charAt(0)}</span>
                        </div>
                        <div>
                          <div style={{ fontWeight: 600 }}>{u.firstname} {u.lastname}</div>
                          <div style={{ fontSize: "0.75rem", color: T.muted }}>{u.email}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {form.issuedToUserId && (
                  <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ background: T.greenBg, border: `1px solid ${T.greenBorder}`, color: T.green, padding: "2px 10px", borderRadius: 99, fontFamily: T.fontBody, fontSize: "0.63rem" }}>User selected</span>
                    <button onClick={() => { setForm({ ...form, issuedToUserId: "" }); setUserSearch(""); }} style={{ background: "none", border: "none", color: T.muted, cursor: "pointer", fontSize: "0.75rem", fontFamily: T.fontBody }}>Clear</button>
                  </div>
                )}
              </div>

              <div>
                <Label>Discount Type</Label>
                <FieldSelect value={form.discountType} onChange={(e) => setForm({ ...form, discountType: e.target.value })}>
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed_amount">Fixed Amount ($)</option>
                </FieldSelect>
              </div>

              <div>
                <Label>{form.discountType === "percentage" ? "Discount (%)" : "Amount (USD $)"}</Label>
                <FieldInput
                  type="number"
                  value={form.discountValue}
                  placeholder={form.discountType === "percentage" ? "e.g., 20" : "e.g., 10.00"}
                  onChange={(e) => setForm({ ...form, discountValue: e.target.value })}
                />
              </div>

              <div>
                <Label>Expiry Date (optional)</Label>
                <FieldInput type="date" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
              </div>

              <div>
                <Label>Max Uses</Label>
                <FieldInput type="number" value={form.maxUses} onChange={(e) => setForm({ ...form, maxUses: Number(e.target.value) })} />
              </div>

              <div>
                <Label>Plan Context (optional)</Label>
                <FieldSelect value={form.planContext} onChange={(e) => setForm({ ...form, planContext: e.target.value })}>
                  <option value="">Any plan</option>
                  <option value="individual">Individual only</option>
                  <option value="organization">Company only</option>
                </FieldSelect>
              </div>

              <div>
                <Label>Admin Notes (internal)</Label>
                <FieldInput value={form.notes} placeholder="Optional internal note" onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>

            <div style={{ marginTop: 16, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontFamily: T.fontBody, fontSize: "0.84rem", color: T.dark }}>
                <input
                  type="checkbox"
                  checked={form.sendEmail}
                  disabled={!form.issuedToUserId}
                  onChange={(e) => setForm({ ...form, sendEmail: e.target.checked })}
                  style={{ width: 15, height: 15, accentColor: T.amber }}
                />
                <FaEnvelope style={{ color: form.issuedToUserId ? T.amber : T.mutedLight, fontSize: 13 }} />
                Send code to user via email immediately
                {!form.issuedToUserId && <span style={{ fontFamily: T.fontBody, fontSize: "0.6rem", color: T.muted }}>(select a user first)</span>}
              </label>
              <Btn onClick={generate} disabled={generating || !form.discountValue}>
                <FaTag size={11} />
                {generating ? "Generating…" : "Generate & Save"}
              </Btn>
            </div>
          </div>
        )}

        {/* Coupon table */}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: T.bg, borderBottom: `2px solid ${T.border}` }}>
                {["Code", "Issued To", "Discount", "Status", "Expiry", "Usage", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "11px 16px", textAlign: "left", fontFamily: T.fontBody, fontSize: "0.58rem", letterSpacing: "0.12em",  color: T.muted, fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ padding: "40px" }}><Spinner /></td></tr>
              ) : coupons.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: "56px 24px", textAlign: "center" }}>
                    <MdOutlineSearchOff style={{ fontSize: 44, color: T.mutedLight, display: "block", margin: "0 auto 10px" }} />
                    <p style={{ fontFamily: T.fontBody, fontWeight: 600, color: T.dark, margin: 0 }}>No coupons found</p>
                    <p style={{ fontFamily: T.fontBody, fontSize: "0.82rem", color: T.muted, margin: "4px 0 0" }}>Generate your first coupon above</p>
                  </td>
                </tr>
              ) : coupons.map((c) => (
                <tr key={c.id} className="asub-row" style={{ borderBottom: `1px solid ${T.border}`, background: T.surface }}>
                  <td style={{ padding: "12px 16px" }}>
                    <code style={{ fontFamily: T.fontBody, fontSize: "0.82rem", fontWeight: 700, color: T.dark, background: T.bg, padding: "3px 8px", borderRadius: 5, border: `1px solid ${T.border}`, letterSpacing: "0.06em" }}>{c.code}</code>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    {c.issuedToUser ? (
                      <div>
                        <div style={{ fontFamily: T.fontBody, fontWeight: 600, color: T.dark, fontSize: "0.84rem" }}>{c.issuedToUser.firstname} {c.issuedToUser.lastname}</div>
                        <div style={{ fontFamily: T.fontBody, fontSize: "0.62rem", color: T.muted }}>{c.issuedToUser.email}</div>
                      </div>
                    ) : (
                      <span style={{ fontFamily: T.fontBody, fontSize: "0.65rem", color: T.muted }}>Public</span>
                    )}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ fontFamily: T.fontBody, fontWeight: 700, color: T.dark, fontSize: "0.86rem" }}>
                      {c.discountType === "percentage" ? `${c.discountValue}%` : `$${Number(c.discountValue).toFixed(2)}`}
                    </div>
                    <div style={{ fontFamily: T.fontBody, fontSize: "0.6rem", color: T.muted, textTransform: "capitalize" }}>{c.discountType.replace("_", " ")} · once</div>
                  </td>
                  <td style={{ padding: "12px 16px" }}><Badge status={c.status} /></td>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ fontFamily: T.fontBody, fontSize: "0.83rem", color: T.dark }}>{formatDate(c.expiresAt)}</div>
                    {c.emailSentAt && <div style={{ fontFamily: T.fontBody, fontSize: "0.6rem", color: T.green, marginTop: 2 }}>Emailed {formatDate(c.emailSentAt)}</div>}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                        <span style={{ fontFamily: T.fontBody, fontSize: "0.72rem", fontWeight: 700, color: T.dark }}>{c.timesUsed ?? 0}</span>
                        <span style={{ fontFamily: T.fontBody, fontSize: "0.6rem", color: T.muted }}>/ {c.maxUses} uses</span>
                      </div>
                      <div style={{ height: 4, borderRadius: 99, background: "rgba(0,0,0,0.08)", overflow: "hidden", width: 64 }}>
                        <div style={{
                          height: "100%",
                          width: `${Math.min(100, Math.round(((c.timesUsed ?? 0) / c.maxUses) * 100))}%`,
                          background: (c.timesUsed ?? 0) >= c.maxUses ? T.green : T.amber,
                          borderRadius: 99,
                        }} />
                      </div>
                      {c.usedByUser && (
                        <div style={{ marginTop: 2 }}>
                          <div style={{ fontFamily: T.fontBody, fontSize: "0.8rem", color: T.dark }}>{c.usedByUser.email}</div>
                          <div style={{ fontFamily: T.fontBody, fontSize: "0.58rem", color: T.muted }}>{formatDate(c.usedAt)}</div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {c.status === "active" && c.issuedToUser && (
                        <Btn onClick={() => sendEmail(c.id)} variant="ghost" small><FaEnvelope size={11} /> Email</Btn>
                      )}
                      {c.status === "active" && (
                        <Btn onClick={() => revoke(c.id)} variant="red" small><FaBan size={11} /> Revoke</Btn>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   TAB 4 — SUBSCRIBERS (enhanced from original)
   ════════════════════════════════════════════════════════════════ */
function SubscribersPanel() {
  const [q, setQ]             = useState("");
  const [statusFilter, setFilter] = useState("all");
  const [rows, setRows]       = useState([]);
  const [stats, setStats]     = useState({ total: 0, active: 0, trialing: 0, pastDue: 0, canceled: 0 });
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      const r = await fetch(`${API}/api/admin/plans/subscribers?${params}`, { credentials: "include" });
      const d = await r.json();
      const items = d.items || [];
      setRows(items);
      setStats({
        total:    items.length,
        active:   items.filter((i) => i.status === "active").length,
        trialing: items.filter((i) => i.status === "trialing").length,
        pastDue:  items.filter((i) => i.status === "past_due").length,
        canceled: items.filter((i) => i.status === "canceled").length,
      });
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = statusFilter === "all" ? rows : rows.filter((r) => r.status === statusFilter);

  const KPI_CARDS = [
    { label: "Total",     value: stats.total,    accent: T.dark,      key: "all" },
    { label: "Active",    value: stats.active,   accent: T.green,     key: "active" },
    { label: "Trialing",  value: stats.trialing, accent: T.amberDark, key: "trialing" },
    { label: "Past Due",  value: stats.pastDue,  accent: "#c2410c",   key: "past_due" },
    { label: "Canceled",  value: stats.canceled, accent: T.red,       key: "canceled" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px,1fr))", gap: 12 }}>
        {KPI_CARDS.map(({ label, value, accent, key }) => (
          <div key={key} onClick={() => setFilter(key)} style={{ background: T.surface, borderRadius: 8, padding: "14px 16px", border: `1px solid ${statusFilter === key ? accent : T.border}`, borderLeft: `3px solid ${accent}`, boxShadow: "0 1px 3px rgba(0,0,0,0.05)", cursor: "pointer", transition: "border-color 0.15s" }}>
            <p style={{ fontFamily: T.fontBody, fontSize: "0.58rem", letterSpacing: "0.1em",  color: T.muted, margin: 0 }}>{label}</p>
            <p style={{ fontFamily: T.fontBody, fontSize: "1.8rem", fontWeight: 700, color: T.dark, margin: "4px 0 0", lineHeight: 1 }}>{value}</p>
          </div>
        ))}
      </div>

      <div style={{ background: T.surface, borderRadius: 10, border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${T.border}` }}>
          <h2 style={{ fontFamily: T.fontBody, fontSize: "1.2rem", letterSpacing: "0.04em", color: T.dark, margin: "0 0 14px" }}>All Subscribers</h2>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
              <FaSearch style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: T.mutedLight, fontSize: 13 }} />
              <input className="asub-input" style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px 9px 34px", borderRadius: 7, border: `1px solid ${T.border}`, fontFamily: T.fontBody, fontSize: "0.84rem", color: T.dark, background: T.surface }} placeholder="Search by email…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
            </div>
            <Btn onClick={load} disabled={loading} variant="ghost">
              {loading ? "…" : "Search"}
            </Btn>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: T.bg, borderBottom: `2px solid ${T.border}` }}>
                {["Subscriber", "Scope", "Plan", "Status", "Trial / Period End", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "11px 16px", textAlign: "left", fontFamily: T.fontBody, fontSize: "0.58rem", letterSpacing: "0.12em",  color: T.muted, fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ padding: 40 }}><Spinner /></td></tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: "56px 24px", textAlign: "center" }}>
                    <MdOutlineSearchOff style={{ fontSize: 44, color: T.mutedLight, display: "block", margin: "0 auto 10px" }} />
                    <p style={{ fontFamily: T.fontBody, fontWeight: 600, color: T.dark, margin: 0 }}>No subscribers found</p>
                  </td>
                </tr>
              ) : filtered.map((sub) => <SubscriberRow key={sub.id} subscriber={sub} />)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SubscriberRow({ subscriber: s }) {
  const initials = s.user?.email?.charAt(0).toUpperCase() || "?";
  const dl = s.trialEnd ? daysLeft(s.trialEnd) : null;

  return (
    <tr className="asub-row" style={{ borderBottom: `1px solid ${T.border}`, background: T.surface }}>
      <td style={{ padding: "12px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: "50%", background: `linear-gradient(135deg, ${T.amber} 0%, ${T.amberDark} 100%)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontFamily: T.fontBody, fontSize: "1rem", color: T.white }}>{initials}</div>
          <div>
            <div style={{ fontFamily: T.fontBody, fontWeight: 600, color: T.dark, fontSize: "0.85rem" }}>{s.user?.email || "—"}</div>
            <div style={{ fontFamily: T.fontBody, fontSize: "0.6rem", color: T.muted }}>ID: {s.id.slice(0, 8)}…</div>
          </div>
        </div>
      </td>
      <td style={{ padding: "12px 16px" }}>
        <span style={{ fontFamily: T.fontBody, fontSize: "0.65rem", padding: "2px 8px", borderRadius: 99, background: s.scope === "org" ? T.amberBg : "rgba(99,102,241,0.1)", color: s.scope === "org" ? T.amberDark : "#4f46e5", border: `1px solid ${s.scope === "org" ? T.amberBorder : "rgba(99,102,241,0.25)"}` }}>
          {s.scope === "org" ? "Company" : "Individual"}
        </span>
      </td>
      <td style={{ padding: "12px 16px" }}>
        <div style={{ fontFamily: T.fontBody, fontWeight: 600, color: T.dark, fontSize: "0.85rem" }}>{s.plan?.name || "—"}</div>
        <div style={{ fontFamily: T.fontBody, fontSize: "0.6rem", color: T.muted }}>{s.plan?.code || ""}</div>
      </td>
      <td style={{ padding: "12px 16px" }}><Badge status={s.status} /></td>
      <td style={{ padding: "12px 16px" }}>
        {s.status === "trialing" && s.trialEnd ? (
          <div>
            <div style={{ fontFamily: T.fontBody, fontSize: "0.83rem", color: T.dark }}>Trial ends {formatDate(s.trialEnd)}</div>
            <div style={{ fontFamily: T.fontBody, fontSize: "0.6rem", color: dl <= 2 ? T.red : T.amberDark, marginTop: 2 }}>
              {dl === 0 ? "Expires today" : `${dl} day${dl !== 1 ? "s" : ""} left`}
            </div>
          </div>
        ) : (
          <div style={{ fontFamily: T.fontBody, fontSize: "0.83rem", color: T.dark }}>{formatDate(s.currentPeriodEnd)}</div>
        )}
      </td>
      <td style={{ padding: "12px 16px" }}>
        <Btn variant="ghost" small>View Details</Btn>
      </td>
    </tr>
  );
}


/* ════════════════════════════════════════════════════════════════
   TAB 5 — FREE TRIALS
   Shows all users/orgs currently on an active free trial with no
   paid Stripe subscription. Fetches from /api/admin/plans/trial-users.
   ════════════════════════════════════════════════════════════════ */
function FreeTrialsPanel() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ]             = useState("");
  const toast = useToast();

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      const r = await fetch(`${API}/api/admin/plans/trial-users?${params}`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setRows(d.items || []);
    } catch (e) {
      toast.error("Failed to load trial users");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const activeToday  = rows.filter((r) => daysLeft(r.trialEndsAt) === 0);
  const expiringSoon = rows.filter((r) => { const d = daysLeft(r.trialEndsAt); return d > 0 && d <= 3; });

  const STAT_CARDS = [
    { label: "Active Trials", value: rows.length,          accent: T.amberDark, },
    { label: "Expire Today",  value: activeToday.length,   accent: T.red,       },
    { label: "Expire ≤ 3 days", value: expiringSoon.length, accent: "#c2410c",   },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Info banner */}
      <div style={{ background: T.amberBg, border: `1px solid ${T.amberBorder}`, borderRadius: 10, padding: "16px 20px", display: "flex", alignItems: "flex-start", gap: 14 }}>
        <LuTimer style={{ color: T.amberDark, fontSize: 20, flexShrink: 0, marginTop: 2 }} />
        <div>
          <p style={{ fontFamily: T.fontBody, fontWeight: 700, color: T.dark, margin: "0 0 4px", fontSize: "0.9rem" }}>Active Free Trials</p>
          <p style={{ fontFamily: T.fontBody, fontSize: "0.82rem", color: T.muted, margin: 0, lineHeight: 1.5 }}>
            Users and organizations currently in their free trial with no paid subscription. Sorted by soonest expiry. Trial configuration is managed in the <strong>Trial Config</strong> tab.
          </p>
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px,1fr))", gap: 12 }}>
        {STAT_CARDS.map(({ label, value, accent }) => (
          <div key={label} style={{ background: T.surface, borderRadius: 8, padding: "14px 16px", border: `1px solid ${T.border}`, borderLeft: `3px solid ${accent}`, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <p style={{ fontFamily: T.fontBody, fontSize: "0.58rem", letterSpacing: "0.1em",  color: T.muted, margin: 0 }}>{label}</p>
            <p style={{ fontFamily: T.fontBody, fontSize: "1.8rem", fontWeight: 700, color: T.dark, margin: "4px 0 0", lineHeight: 1 }}>{value}</p>
          </div>
        ))}
      </div>

      <div style={{ background: T.surface, borderRadius: 10, border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        {/* Search toolbar */}
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${T.border}` }}>
          <h2 style={{ fontFamily: T.fontBody, fontSize: "1.2rem", letterSpacing: "0.04em", color: T.dark, margin: "0 0 14px" }}>
            Active Trial Accounts — {rows.length}
          </h2>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
              <FaSearch style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: T.mutedLight, fontSize: 13 }} />
              <input
                className="asub-input"
                style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px 9px 34px", borderRadius: 7, border: `1px solid ${T.border}`, fontFamily: T.fontBody, fontSize: "0.84rem", color: T.dark, background: T.surface }}
                placeholder="Search by name, email or company…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
              />
            </div>
            <Btn onClick={load} disabled={loading} variant="ghost">{loading ? "…" : "Search"}</Btn>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: T.bg, borderBottom: `2px solid ${T.border}` }}>
                {["Account", "Scope", "Trial Started", "Trial Ends", "Days Left", "Features", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "11px 16px", textAlign: "left", fontFamily: T.fontBody, fontSize: "0.58rem", letterSpacing: "0.12em",  color: T.muted, fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ padding: 40 }}><Spinner /></td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: "56px 24px", textAlign: "center" }}>
                    <MdOutlineSearchOff style={{ fontSize: 44, color: T.mutedLight, display: "block", margin: "0 auto 10px" }} />
                    <p style={{ fontFamily: T.fontBody, fontWeight: 600, color: T.dark, margin: 0 }}>No active trial accounts found</p>
                    <p style={{ fontFamily: T.fontBody, fontSize: "0.82rem", color: T.muted, margin: "4px 0 0" }}>All trial users have either converted to paid plans or their trials have expired</p>
                  </td>
                </tr>
              ) : rows.map((row) => {
                const dl = daysLeft(row.trialEndsAt);
                const urgentColor = dl === 0 ? T.red : dl <= 3 ? "#c2410c" : T.amberDark;
                const initials = (row.name || row.email || "?").charAt(0).toUpperCase();
                return (
                  <tr key={row.id} className="asub-row" style={{ borderBottom: `1px solid ${T.border}`, background: T.surface }}>
                    {/* Account */}
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 34, height: 34, borderRadius: "50%", background: T.amberBg, border: `1px solid ${T.amberBorder}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontFamily: T.fontBody, fontSize: "1rem", color: T.amberDark }}>
                          {initials}
                        </div>
                        <div>
                          <div style={{ fontFamily: T.fontBody, fontWeight: 600, color: T.dark, fontSize: "0.85rem" }}>
                            {row.scope === "org" ? row.name : `${row.name}`}
                          </div>
                          <div style={{ fontFamily: T.fontBody, fontSize: "0.6rem", color: T.muted }}>{row.email || "—"}</div>
                        </div>
                      </div>
                    </td>
                    {/* Scope */}
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{ fontFamily: T.fontBody, fontSize: "0.65rem", padding: "2px 8px", borderRadius: 99, background: row.scope === "org" ? T.amberBg : "rgba(99,102,241,0.1)", color: row.scope === "org" ? T.amberDark : "#4f46e5", border: `1px solid ${row.scope === "org" ? T.amberBorder : "rgba(99,102,241,0.25)"}` }}>
                        {row.scope === "org" ? "Company" : "Individual"}
                      </span>
                    </td>
                    {/* Trial started */}
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ fontFamily: T.fontBody, fontSize: "0.83rem", color: T.dark }}>{formatDate(row.trialStartedAt)}</div>
                    </td>
                    {/* Trial ends */}
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ fontFamily: T.fontBody, fontSize: "0.83rem", color: T.dark }}>{formatDate(row.trialEndsAt)}</div>
                    </td>
                    {/* Days left */}
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{ fontFamily: T.fontBody, fontSize: "0.72rem", fontWeight: 700, color: urgentColor }}>
                        {dl === 0 ? "Expires today" : `${dl} day${dl !== 1 ? "s" : ""}`}
                      </span>
                    </td>
                    {/* Features */}
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {(row.trialFeatures || []).map((f) => (
                          <span key={f} style={{ fontFamily: T.fontBody, fontSize: "0.58rem", padding: "2px 6px", borderRadius: 4, background: T.greenBg, color: T.green, border: `1px solid ${T.greenBorder}`, letterSpacing: "0.04em" }}>
                            {f.replace("feat.", "")}
                          </span>
                        ))}
                      </div>
                    </td>
                    {/* Actions */}
                    <td style={{ padding: "12px 16px" }}>
                      <Btn
                        variant="amber"
                        small
                        onClick={() => toast.info("To issue a coupon to this user, go to the Coupons tab and search for their email.")}
                      >
                        <FaTag size={10} /> Issue Coupon
                      </Btn>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   TAB 6 — EXPIRED / LAPSED (NEW)
   ════════════════════════════════════════════════════════════════ */
function ExpiredPanel() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ]             = useState("");
  const toast = useToast();

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: "incomplete" });
      if (q) params.set("q", q);
      const r = await fetch(`${API}/api/admin/plans/subscribers?${params}`, { credentials: "include" });
      const d = await r.json();
      // also grab trialing-but-expired from subscriber list
      const all = (d.items || []).filter((i) => i.status === "incomplete" || i.status === "canceled");
      setRows(all);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function issueCoupon(userId) {
    // Navigate admin to coupons tab with this user pre-selected (simple approach)
    toast.info(`To issue a coupon to this user, go to the Coupons tab and search for their email.`);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Info banner */}
      <div style={{ background: T.amberBg, border: `1px solid ${T.amberBorder}`, borderRadius: 10, padding: "16px 20px", display: "flex", alignItems: "flex-start", gap: 14 }}>
        <FaClock style={{ color: T.amberDark, fontSize: 20, flexShrink: 0, marginTop: 2 }} />
        <div>
          <p style={{ fontFamily: T.fontBody, fontWeight: 700, color: T.dark, margin: "0 0 4px", fontSize: "0.9rem" }}>Expired Trials & Lapsed Subscriptions</p>
          <p style={{ fontFamily: T.fontBody, fontSize: "0.82rem", color: T.muted, margin: 0, lineHeight: 1.5 }}>
            These users had their trial expire or subscription lapse. Their access is fully blocked. Consider sending them a targeted coupon to encourage re-engagement.
          </p>
        </div>
      </div>

      <div style={{ background: T.surface, borderRadius: 10, border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${T.border}` }}>
          <h2 style={{ fontFamily: T.fontBody, fontSize: "1.2rem", letterSpacing: "0.04em", color: T.dark, margin: "0 0 14px" }}>
            Expired / Lapsed — {rows.length} account{rows.length !== 1 ? "s" : ""}
          </h2>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
              <FaSearch style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: T.mutedLight, fontSize: 13 }} />
              <input className="asub-input" style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px 9px 34px", borderRadius: 7, border: `1px solid ${T.border}`, fontFamily: T.fontBody, fontSize: "0.84rem", color: T.dark, background: T.surface }} placeholder="Search by email…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
            </div>
            <Btn onClick={load} disabled={loading} variant="ghost">{loading ? "…" : "Search"}</Btn>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: T.bg, borderBottom: `2px solid ${T.border}` }}>
                {["User", "Scope", "Plan", "Status", "Expired / Lapsed At", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "11px 16px", textAlign: "left", fontFamily: T.fontBody, fontSize: "0.58rem", letterSpacing: "0.12em",  color: T.muted, fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ padding: 40 }}><Spinner /></td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: "56px 24px", textAlign: "center" }}>
                    <FaCheck style={{ fontSize: 36, color: T.green, display: "block", margin: "0 auto 10px" }} />
                    <p style={{ fontFamily: T.fontBody, fontWeight: 600, color: T.dark, margin: 0 }}>No expired accounts</p>
                    <p style={{ fontFamily: T.fontBody, fontSize: "0.82rem", color: T.muted, margin: "4px 0 0" }}>All users have active or trialing subscriptions</p>
                  </td>
                </tr>
              ) : rows.map((s) => (
                <tr key={s.id} className="asub-row" style={{ borderBottom: `1px solid ${T.border}`, background: T.surface }}>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 34, height: 34, borderRadius: "50%", background: T.bg, border: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontFamily: T.fontBody, fontSize: "1rem", color: T.muted }}>{s.user?.email?.charAt(0).toUpperCase() || "?"}</div>
                      <div>
                        <div style={{ fontFamily: T.fontBody, fontWeight: 600, color: T.dark, fontSize: "0.85rem" }}>{s.user?.email || "—"}</div>
                        <div style={{ fontFamily: T.fontBody, fontSize: "0.6rem", color: T.muted }}>ID: {s.id.slice(0, 8)}…</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{ fontFamily: T.fontBody, fontSize: "0.65rem", padding: "2px 8px", borderRadius: 99, background: s.scope === "org" ? T.amberBg : "rgba(99,102,241,0.1)", color: s.scope === "org" ? T.amberDark : "#4f46e5", border: `1px solid ${s.scope === "org" ? T.amberBorder : "rgba(99,102,241,0.25)"}` }}>
                      {s.scope === "org" ? "Company" : "Individual"}
                    </span>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ fontFamily: T.fontBody, fontSize: "0.84rem", color: T.dark }}>{s.plan?.name || "—"}</div>
                  </td>
                  <td style={{ padding: "12px 16px" }}><Badge status={s.status} /></td>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ fontFamily: T.fontBody, fontSize: "0.83rem", color: T.dark }}>{formatDate(s.currentPeriodEnd || s.trialEnd)}</div>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <Btn onClick={() => issueCoupon(s.user?.id)} variant="amber" small>
                      <FaTag size={10} /> Issue Coupon
                    </Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ── Expandable Section (preserved from original) ───────────────── */
function ExpandableSection({ title, subtitle, children, isExpanded, onToggle }) {
  return (
    <div style={{ borderTop: `1px solid ${T.border}` }}>
      <button className="asub-exp" onClick={onToggle} style={{ width: "100%", padding: "13px 22px", textAlign: "left", background: "none", border: "none", cursor: "pointer", transition: "background 0.12s" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <p style={{ fontFamily: T.fontBody, fontWeight: 700, color: T.dark, margin: 0, fontSize: "0.88rem" }}>{title}</p>
            <p style={{ fontFamily: T.fontBody, fontSize: "0.78rem", color: T.muted, margin: "2px 0 0" }}>{subtitle}</p>
          </div>
          <span style={{ color: T.mutedLight, fontSize: "0.68rem", fontFamily: T.fontBody }}>{isExpanded ? "▲" : "▼"}</span>
        </div>
      </button>
      {isExpanded && <div style={{ padding: "4px 22px 18px" }}>{children}</div>}
    </div>
  );
}